import React, {
	useCallback, useEffect, useRef, useState,
} from 'react';
import {notification, Popconfirm} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import {useWindowSize} from 'react-use';
import ProfilesSelect from './components/ProfilesSelect';
import {getPaymentProfile} from '../../store/actions/userActions';
import ProfileTypes from './components/ProfileTypes';
import ProfilesForm from './components/ProfilesForm';
import {scrollBar} from '../../styles/sc-mixins';
import ProfileFileUploader from './components/ProfileFileUploader';
import ProfilesImage from './components/ProfilesImage';
import Modal from '../../components/Modal';
import {
	createProfile, deleteProfile, editProfile, getListToCreate,
} from '../../store/actions/profilesActions';
import {clearResult, setUserProperty} from '../../store/reducers/userSlice';

const StyledWrapper = styled.div`
padding: 30px 0.9375rem 110px;
  height:  ${({height}) => height - 200}px;
  overflow-y: auto;
  ${scrollBar()};
 
`;

const StyledSelects = styled.div`
  display: flex;
  align-items: center;
  gap:30px;
  flex-wrap: wrap;
	.top__buttons {
		display: flex;
	 	align-items: center;
	    gap:30px;
	}
@media screen and (max-width: 1200px) {
grid-template-columns: 1fr 1fr;
.top__buttons {
		grid-column: span 2;
		justify-content: flex-end;
	}
}
`;

const StyledImageContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledDeleteButton = styled.button`
  position: absolute;
  top: 15px;
  right: 10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ff4d4f;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  padding: 0;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: #ff7875;
    transform: scale(1.1);
  }
`;

const numericValues = ['PHONE', 'BIK', 'CORRESPONDENT_ACCOUNT', 'PAYMENT_ACCOUNT', 'INN', 'OGRNIP'];

const Profiles = () => {
	const {height} = useWindowSize();
	const dispatch = useDispatch();
	const {
		profileStamp, profileSignature, currentProfileId,
		paymentProfile, currentProfileType, profileTypes,
		result,
	} = useSelector((state) => state.user);
	const modal = useRef(null);
	const [name, setName] = useState('');
	const [currentProfile, setCurrentProfile] = useState(null);
	const [profileType, setProfileType] = useState(null);
	const [stamp, setStamp] = useState('');
	const [signature, setSignature] = useState('');
	const [changes, setChanges] = useState({});
	const [canSave, setCanSave] = useState(true);
	const [isDeletingStamp, setIsDeletingStamp] = useState(false);
	const [isDeletingSignature, setIsDeletingSignature] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	// Добавляем состояния для определения наличия полей в профиле
	const [hasStampField, setHasStampField] = useState(false);
	const [hasSignatureField, setHasSignatureField] = useState(false);

	const inputHandler = (id, code) => (evt) => {
		let {value} = evt.target;

		if (numericValues.includes(code)) {
			value = value.toString().replace(/[\D]/ig, '');
		}
		changes[id] = value;
		const newChanges = {...changes, [id]: value};
		setChanges(newChanges);
	};

	const openDialog = () => {
		modal.current.classList.remove('page__modal');
	};

	const closeDialog = () => {
		modal.current.classList.add('page__modal');
	};

	const renameHandler = (evt) => {
		const {value} = evt.target;
		setName(value);
	};

	const addProfileHandler = useCallback(() => {
		if (name && profileType) {
			dispatch(createProfile({name, id_profile: profileType.value}));
			closeDialog();
		}
	}, [dispatch, name, profileType]);

	const deleteStampHandler = useCallback(() => {
		setIsDeletingStamp(true);
		setStamp('');
	}, []);

	const deleteSignatureHandler = useCallback(() => {
		setIsDeletingSignature(true);
		setSignature('');
	}, []);

	const submitHandler = useCallback(async () => {
		if (isSubmitting) return;
		
		setIsSubmitting(true);
		const profile = new FormData();
		profile.append('id', currentProfileId);
		profile.append('name', name);
		
		let finalChanges = {...changes};
		
		const fullNameProp = currentProfile?.props?.find(prop => prop.code === 'NAME_PROFILE');
		const shortNameProp = currentProfile?.props?.find(prop => prop.code === 'NAME_PROFILE_SHORT');
		
		const fullNameValue = finalChanges[fullNameProp?.id] !== undefined 
			? finalChanges[fullNameProp?.id] 
			: fullNameProp?.value;
		
		const shortNameValue = finalChanges[shortNameProp?.id] !== undefined 
			? finalChanges[shortNameProp?.id] 
			: shortNameProp?.value;
		
		if (fullNameValue && !shortNameValue && shortNameProp) {
			finalChanges[shortNameProp.id] = fullNameValue;
			setChanges(finalChanges);
		}
		
		Object.keys(finalChanges).forEach((key) => {
			profile.append(key, finalChanges[key]);
		});

		// Добавляем флаги удаления если они были установлены
		if (isDeletingStamp) {
			profile.append('delete_stamp', '1');
			profile.append('profile_id', currentProfileId);
		}
		if (isDeletingSignature) {
			profile.append('delete_signature', '1');
			profile.append('profile_id', currentProfileId);
		}

		if (profileStamp && !isDeletingStamp) {
			profile.append('stamp', profileStamp);
		}
		if (profileSignature && !isDeletingSignature) {
			profile.append('signature', profileSignature);
		}

		// Сохраняем изменения
		const result = await dispatch(editProfile({profile}));
		
		// Если сохранение успешно, обновляем данные
		if (result?.payload?.result === true) {
			// Очищаем URL объекты
			if (profileStamp) URL.revokeObjectURL(profileStamp);
			if (profileSignature) URL.revokeObjectURL(profileSignature);
			
			// Обновляем список профилей
			await dispatch(getPaymentProfile());
			
			// Сбрасываем флаги удаления и изменения
			setIsDeletingStamp(false);
			setIsDeletingSignature(false);
			setChanges({});
			
			// Очищаем временные файлы
			dispatch(setUserProperty({property: 'profileSignature', value: null}));
			dispatch(setUserProperty({property: 'profileStamp', value: null}));
		}
		
		setIsSubmitting(false);
		closeDialog();
	}, [currentProfileId, changes, profileStamp, profileSignature, dispatch, name, currentProfile, isDeletingStamp, isDeletingSignature, isSubmitting]);

	const deleteProfileHandler = useCallback(() => {
		dispatch(deleteProfile({id: currentProfileId}));
	}, [currentProfileId, dispatch]);

	useEffect(() => {
		let canSave = true;
		if (currentProfile) {
			currentProfile.props.forEach((prop) => {
				const isRequired = prop.required === 'Y';
				if (isRequired) {
					const value = typeof changes[prop.id] !== 'undefined'
						? changes[prop.id]
						: prop.value;
					canSave = canSave && !!value;
				}
			});
		}
		setCanSave(canSave);
	}, [changes, currentProfile]);

	// Функция для обновления данных из профиля
	const updateProfileData = useCallback(() => {
		const profile = paymentProfile.find((p) => +p.id === +currentProfileId);
		if (profile) {
			setName(profile.name);
			setCurrentProfile(profile);
			
			const currentProfileStamp = profile.props?.find((prop) => prop.code === 'STAMP');
			const currentProfileSignature = profile.props?.find((prop) => prop.code === 'SIGNATURE');
			
			// Определяем наличие полей в профиле
			setHasStampField(!!currentProfileStamp);
			setHasSignatureField(!!currentProfileSignature);
			
			// Обновляем stamp
			if (currentProfileStamp?.value) {
				setStamp(window.location.origin + currentProfileStamp.value);
			} else {
				setStamp('');
			}
			
			// Обновляем signature
			if (currentProfileSignature?.value) {
				setSignature(window.location.origin + currentProfileSignature.value);
			} else {
				setSignature('');
			}
		} else {
			setName('');
			setCurrentProfile(null);
			setStamp('');
			setSignature('');
			setHasStampField(false);
			setHasSignatureField(false);
		}
	}, [currentProfileId, paymentProfile]);

	useEffect(() => {
		updateProfileData();
	}, [updateProfileData]);

	// Эффект для сброса флагов удаления при смене профиля
	useEffect(() => {
		setIsDeletingStamp(false);
		setIsDeletingSignature(false);
		setChanges({});
	}, [currentProfileId]);

	useEffect(() => {
		dispatch(getPaymentProfile());
	}, [dispatch]);

	useEffect(() => {
		if (currentProfileType) {
			const profileType = profileTypes.find((type) => type.value === currentProfileType);
			setProfileType(profileType);
		} else {
			setProfileType(null);
		}
	}, [currentProfileType, profileTypes]);
	
	useEffect(() => {
		if (profileStamp) {
			// Очищаем старый URL если он был
			if (stamp && stamp.startsWith('blob:')) {
				URL.revokeObjectURL(stamp);
			}
			setStamp(URL.createObjectURL(profileStamp));
		}
	}, [profileStamp]);

	useEffect(() => {
		if (profileSignature) {
			// Очищаем старый URL если он был
			if (signature && signature.startsWith('blob:')) {
				URL.revokeObjectURL(signature);
			}
			setSignature(URL.createObjectURL(profileSignature));
		}
	}, [profileSignature]);

	useEffect(() => {
		dispatch(getListToCreate());
	}, [dispatch]);

	useEffect(() => {
		if (result) {
			notification.success({
				message: result,
				placement: 'bottomLeft',
				duration: 5,
			});
			dispatch(clearResult());
		}
	}, [dispatch, result]);
	
	// Очистка URL объектов при размонтировании
	useEffect(() => {
		return () => {
			if (stamp && stamp.startsWith('blob:')) {
				URL.revokeObjectURL(stamp);
			}
			if (signature && signature.startsWith('blob:')) {
				URL.revokeObjectURL(signature);
			}
		};
	}, []);
	
	return (
		<StyledWrapper height={height}>

			<div className="cart__list-header mb-3" style={{fontSize: 18}}>
				Платежный профиль
				пользователя
			</div>
			<StyledSelects>

				{
					!!paymentProfile.length
					&& (
						<div className="">
							<div className="select--default">
								<ProfilesSelect/>
							</div>
						</div>
					)
				}
				<div className="">
					<div className="select--default">
						<ProfileTypes/>
					</div>
				</div>
				<div className="top__buttons">

					{
						(currentProfileId || currentProfileType) && (
							<div className="">
								<button
									className="button button--default button--bordered waves-light button--waves"
									type="button"
									onClick={openDialog}
								><span
									className="button__content"
								>
																	{currentProfileId ? 'Переименовать профиль' : 'Далее'}
           </span>
								</button>
							</div>
						)
					}
					{
						currentProfileId && (
							<div className="">
								<Popconfirm
									title="Вы уверены, что хотите удалить профиль？"
									okText="Да"
									cancelText="Нет"
									onConfirm={deleteProfileHandler}
									okButtonProps={{
										className: 'button button--default button--success waves-light button--waves',
									}}
									cancelButtonProps={{
										className: 'button button--default button--bordered waves-light button--waves',
									}}
									className="confirm-popup"
								>
									<button
										className="button button--default button--bordered waves-light button--waves"
										type="button"
									><span className="button__content">Удалить профиль</span>
									</button>
								</Popconfirm>
							</div>
						)
					}
				</div>

			</StyledSelects>

			{
				currentProfileId && (
					<>
						<section className="pb-5 mb-3">
							<h2 className="title title--weight--medium text--middle pb-4 mt-4">
								{
									currentProfile && <>{currentProfile.type_name}: {currentProfile.name}</>
								}
							</h2>
							<div className="row">
								<div className="col-7">
									<ProfilesForm
										inputHandler={inputHandler}
										changes={changes}
										profile={currentProfile}
									/>
								</div>
							</div>
						</section>
						{
							(hasStampField || hasSignatureField) && (
								<section className="pb-5 mb-5">
									<h2 className="title title--weight--medium text--middle pb-4">Факсимиле</h2>
									<div className="row">
										{
											hasSignatureField && (
												<div className="col-auto">
													<ProfileFileUploader
														title="Загрузить подпись"
														fileType="profileSignature"
													/>
												</div>
											)
										}
										{
											hasStampField && (
												<div className="col-auto">
													<ProfileFileUploader
														title="Загрузить печать"
														fileType="profileStamp"
													/>
												</div>
											)
										}
									</div>
									<div className="row">
										{
											hasSignatureField && signature && signature !== '' && (
												<div className="col-auto">
													<StyledImageContainer>
														<ProfilesImage src={signature} alt="подпись"/>
														<Popconfirm
															title="Вы уверены, что хотите удалить подпись？"
															okText="Да"
															cancelText="Нет"
															onConfirm={deleteSignatureHandler}
															okButtonProps={{
																className: 'button button--default button--success waves-light button--waves',
															}}
															cancelButtonProps={{
																className: 'button button--default button--bordered waves-light button--waves',
															}}
															className="confirm-popup"
														>
															<StyledDeleteButton>
																×
															</StyledDeleteButton>
														</Popconfirm>
													</StyledImageContainer>
												</div>
											)
										}
										{
											hasStampField && stamp && stamp !== '' && (
												<div className="col-auto">
													<StyledImageContainer>
														<ProfilesImage src={stamp} alt="печать"/>
														<Popconfirm
															title="Вы уверены, что хотите удалить печать？"
															okText="Да"
															cancelText="Нет"
															onConfirm={deleteStampHandler}
															okButtonProps={{
																className: 'button button--default button--success waves-light button--waves',
															}}
															cancelButtonProps={{
																className: 'button button--default button--bordered waves-light button--waves',
															}}
															className="confirm-popup"
														>
															<StyledDeleteButton>
																×
															</StyledDeleteButton>
														</Popconfirm>
													</StyledImageContainer>
												</div>
											)
										}
									</div>
									<p className="personal__profiles-facsimile-requirements pt-4">
										Формат файла должен быть
										<span className="accent mx-2">
												PNG
											</span>
										или
										<span
											className="accent ml-2"
										>
												JPG
           </span>
									</p>
								</section>
							)
						}
						{
							canSave && !isSubmitting
								? (
									<button
										onClick={submitHandler}
										className="button button--default button--success waves-light button--waves"
										type="submit"
										disabled={isSubmitting}
									><span className="button__content">
										{isSubmitting ? 'Сохранение...' : 'Сохранить изменения'}
									</span>
									</button>
								)
								: (
									<button
										className="button button--default waves-light button--waves button--gray"
										type="button"
										disabled
									><span className="button__content">Сохранить изменения</span>
									</button>
								)
						}
					</>
				)
			}

			<Modal
				ref={modal}
				title="Введите имя платежного профиля"
				onClose={closeDialog}
			>
				<div className="mt-5 d-flex align-items-center justify-content-center w-100">
					<input className="form__field" type="text" value={name} onChange={renameHandler}/>
				</div>
				<div className="mt-5 d-flex align-items-center justify-content-center">
					<button
						className="button button--success waves-light button--waves button--default"
						type="submit"
						onClick={currentProfileId ? submitHandler : addProfileHandler}
					><span className="button__content">Сохранить</span>
					</button>
				</div>
			</Modal>
		</StyledWrapper>
	);
};

export default Profiles;